import React, { useEffect, useMemo, useState } from 'react';
import type { NPC, Quest, ShopItem, DialogOption } from '../../../shared/NPCData';
import { NPC_CONFIG } from '../../../shared/content/generated/entities.gen';
import { TomeButton, TomePanel } from '../primitives/tome';
import { formatCallingName } from '../theme';
import { NPC_UI_VARS } from './NPC/npcPresentation';
import { NPCInteractionUIManager } from '../npc/NPCInteractionUIManager';
import { DialogueView } from './NPC/DialogueView';
import { ServicesView } from './NPC/ServicesView';
import { TrainingView } from './NPC/TrainingView';
import { NpcTitleBlock } from './NPC/NpcPanelChrome';
import { npcTomeVibe } from './NPC/npcTomeVibe';
import { npcQuestEntryCount } from '../npc/npcQuestEntries';
import type { QuestCompletionNotice } from '../contexts/InteractionContext';
import './NPC/NPCPlayerUI.css';

interface NPCInteractionUIProps {
  npc: NPC;
  playerLevel: number;
  playerGold?: number;
  onDialogSelect?: (option: DialogOption) => void;
  onViewQuests?: () => void;
  onBrowseShop?: () => void;
  /** Legacy callbacks remain compatible; quest/shop actions live in their own windows. */
  onQuestAccept?: (quest: Quest) => void;
  onQuestComplete?: (quest: Quest) => void;
  onShopPurchase?: (item: ShopItem) => void;
  pendingAbilityId?: string | null;
  trainingRefusal?: { abilityId: string; message: string } | null;
  onClose?: () => void;
  /** A quest just turned in here (plan 13 §10). */
  completion?: QuestCompletionNotice | null;
  className?: string;
  style?: React.CSSProperties;
}

/** Presentation only; server quotes and the existing interaction owner govern actions. */
export const NPCInteractionUI: React.FC<NPCInteractionUIProps> = ({
  npc, playerLevel, playerGold = 0, onDialogSelect, onViewQuests, onBrowseShop,
  pendingAbilityId, trainingRefusal, onClose, completion, className = '', style,
}) => {
  const [showServices, setShowServices] = useState(false);
  useEffect(() => setShowServices(false), [npc.id]);
  const manager = useMemo(() => new NPCInteractionUIManager({
    npc, playerLevel, callbacks: { onDialogSelect },
  }), [npc, playerLevel, onDialogSelect]);
  const npcTypeInfo = manager.getNPCTypeInfo();
  const trainerClasses = npc.type === 'trainer' ? NPC_CONFIG[npc.id]?.trainerClasses : undefined;
  const headerSubtitle = trainerClasses && trainerClasses.length > 0
    ? `${trainerClasses.map((c) => formatCallingName(c)).join(' · ')} Trainer`
    : npcTypeInfo.name;
  const questEntries = npcQuestEntryCount(npc);
  const hasQuests = questEntries > 0 && Boolean(onViewQuests);
  const hasShop = npc.shopItems.length > 0 && Boolean(onBrowseShop);
  const structuredTraining = npc.trainingOptions !== undefined;
  const hasServices = structuredTraining || npc.services.length > 0;
  const serviceCount = structuredTraining ? npc.trainingOptions!.length : npc.services.length;

  const choices = (hasQuests || hasShop || hasServices) ? <nav className="npc-panel-actions" aria-label="Conversation choices">
    {hasQuests && <TomeButton tone="primary" onClick={onViewQuests} data-controller-return="quests">
      View Quests ({questEntries})
    </TomeButton>}
    {hasShop && <TomeButton tone="secondary" onClick={onBrowseShop} data-controller-return="shop">
      Browse Shop ({npc.shopItems.length})
    </TomeButton>}
    {hasServices && <TomeButton tone="secondary" aria-pressed={showServices}
      onClick={() => setShowServices(value => !value)}>
      {showServices ? 'Conversation' : `${structuredTraining ? 'Training' : 'Services'} (${serviceCount})`}
    </TomeButton>}
  </nav> : undefined;

  return <TomePanel className={`npc-player-panel npc-conversation ${className}`} shapeSeed={`${npc.id}:conversation`} vibe={npcTomeVibe(npc.id)}
    style={{ ...NPC_UI_VARS, zIndex: 200, ...style }} role="dialog"
    aria-label={`Conversation with ${npc.name}`} data-controller-surface="npc-dialogue"
    header={<NpcTitleBlock npcId={npc.id} npcType={npc.type} name={npc.name} pose={npc.portraitPose}
      subtitle={headerSubtitle} onClose={onClose} closeLabel="Close conversation" />}
    toolbar={choices}>
    {showServices && hasServices ? (structuredTraining
      ? <TrainingView options={npc.trainingOptions!} playerLevel={playerLevel} playerGold={playerGold}
        onDialogSelect={onDialogSelect} pendingAbilityId={pendingAbilityId} refusal={trainingRefusal} />
      : <ServicesView npc={npc} npcTypeInfo={npcTypeInfo} onDialogSelect={onDialogSelect} playerGold={playerGold} />)
      : <DialogueView npc={npc} npcTypeInfo={npcTypeInfo} onDialogSelect={onDialogSelect} completion={completion} />}
  </TomePanel>;
};
